import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import { SCreate } from "../../../styles/StyledMUI";
import { textTokens } from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { Permission, usePermissions } from "../../../contexts/app/permissions";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import useRentalProducts from "./hooks/useRentalProducts";
import useEquipmentModelsList from "./hooks/useEquipmentModelsList";
import useRentalModelPricing from "./hooks/useRentalModelPricing";
import RentalProductFormModal from "./display/RentalProductFormModal";
import RentalProductCard from "./display/RentalProductCard";
import RentalUnitPricingCard from "./display/RentalUnitPricingCard";
import type { RentalProduct, RentalProductRequest } from "../../../api/rental/Rental.typed";

export default function RentalProductsPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.RENTAL_CATALOGUE_EDIT);
  const { products, loading, create, update, remove } = useRentalProducts();
  const { models } = useEquipmentModelsList();
  const pricing = useRentalModelPricing();
  const { showConfirmation } = useConfirmationModal();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<RentalProduct | null>(null);

  const modelLabelById = React.useMemo(() => {
    const map = new Map<number, string>();
    models.forEach((m) => {
      if (m.ID != null) map.set(m.ID, m.Name);
    });
    return map;
  }, [models]);

  const handleCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const handleEdit = (p: RentalProduct) => {
    setEditing(p);
    setModalOpen(true);
  };
  const handleSubmit = async (req: RentalProductRequest) => {
    if (editing) await update(editing.id, req);
    else await create(req);
  };
  const handleDelete = async (p: RentalProduct) => {
    const ok = await showConfirmation("Supprimer le kit", `Supprimer « ${p.name} » ?`);
    if (ok) await remove(p.id);
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <DarkTypography level="h4">Location</DarkTypography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          {canEdit && (
            <IconButtonCTA aria-label="Créer un kit de location" onClick={handleCreate}>
              <SCreate />
            </IconButtonCTA>
          )}
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      )}

      {!loading && products.length === 0 && (
        <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
          Aucun kit.
        </DarkTypography>
      )}

      {!loading && products.length > 0 && (
        <RentalProductCard
          items={products}
          modelLabelById={modelLabelById}
          canEdit={canEdit}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {!pricing.loading && (
        <RentalUnitPricingCard rows={pricing.rows} canEdit={canEdit} onSave={pricing.save} />
      )}

      <RentalProductFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
        models={models}
      />
    </Box>
  );
}
